(function () {
    'use strict';

    angular
        .module('app')
        .factory('AttachmentList', AttachmentListFactory);

    AttachmentListFactory.$inject = ['$q', '$filter', 'EmailService', 'DialogService'];

    function AttachmentListFactory($q, $filter, EmailService, DialogService) {

        /**
         * @constructor
         *
         * AttachmentList
         *
         * The attachment list object provides a central area for creating and maintaining an attachment list and its related data.
         *
         * @param {Array} attachments - list of 'file details' which represents the email attachments
         * @param {function} saveEmail - if the email has yet to be persisted, it must be persisted before adding an attachment.
         *                                  The Id property of the resulting promise will be used to populate the emailId variable.
         * @param {number} emailId - id for the email to where the attachments will be saved. A undefined or null value will mean that the email needs to be saved before adding attachments.
         *
         */
        function AttachmentList(attachments, saveEmail, emailId) {
            var statusList = {
                Waiting: 'Waiting',
                Uploading: 'Uploading',
                Error: 'Error',
                Complete: 'Complete',
                Aborted: 'Aborted'
            };
            var vm = this;
            var attachmentList = angular.copy(attachments);

            // File size limits are in bytes
            vm.individualFileSizeLimit = 7340032;
            vm.totalFileSizeLimit = 10485760;

            vm.add = add;
            vm.get = get;
            vm.getAttachmentData = getAttachmentData;
            vm.remove = remove;
            vm.retryUpload = retryUpload;
            vm.cancelUploadsInProgress = cancelUploadsInProgress;
            Object.defineProperty(vm, 'list', { get: function () { return attachmentList; }});
            Object.defineProperty(vm, 'uploadInProgress', { get: getUploadInProgress });
            Object.defineProperty(vm, 'attachmentHasError', { get: getAttachmentHasError });
            Object.defineProperty(vm, 'length', { get: getLength });
            Object.defineProperty(vm, 'totalFileSizeExceeded', { get: getTotalFileSizeExceeded });

            /**
             * Adds the supplied file to the attachment list. This includes persisting the file data.
             *
             * @param {object} file - File details for the file to be added
             *
             */
            function add(file) {
                file.UploadProgress = 0;

                if (file.FileSize > vm.individualFileSizeLimit) {
                    file.Status = statusList.Error;
                    file.ErrorMessage = 'Attachment file size is greater than the ' + $filter('formatFileSize')(vm.individualFileSizeLimit) + ' limit.';
                } else {
                    file.Status = statusList.Waiting;
                }

                if (!_.isNil(emailId)) {
                    attachmentList.push(file);
                    processUploadQueue();
                } else {
                    if (_.isNil(add.saveEmailPromise))
                    {
                        add.saveEmailPromise = saveEmail();
                    }

                    add.saveEmailPromise.then(function (response) {
                        emailId = response.Id;
                        attachmentList.push(file);
                        processUploadQueue();
                    });
                }
            }

            /**
             * @returns {object} returns the file details at a given location within the attachment list.
             */
            function get(index) {
                return attachmentList[index];
            }

            /**
             * Gets the attachment data for the supplied file details.
             *
             * @param {File} file
             * @returns {Promise} A promise which will 'save' the attachment using the clients browser.
             */
            function getAttachmentData(file) {
                return EmailService.getEmailAttachment(file.EmailId, file.DocumentInformationId)
                    .then(getEmailAttachmentOk)
                    .catch(getEmailAttachmentError);

                function getEmailAttachmentOk(data) {
                    saveAs(data, file.FileName);
                }

                function getEmailAttachmentError(response) {
                    DialogService.error('Error retrieving Email attachment', response);
                }
            }

            /**
             * @returns {promise} removes the file with the supplied file details. If the file has been peristed, the file data will be removed. If the file is currently being uploaded, the upload will be aborted.
             */
            function remove(file) {
                if (angular.isDefined(file.Status) && file.Status !== statusList.Aborted) {
                    return $q(function (resolve, reject) {
                        switch (file.Status) {
                            case statusList.Waiting:
                            case statusList.Error:
                                removeFileFromArray(file);
                                break;
                            case statusList.Uploading:
                                if (file.UploadProgress < 100) {
                                    EmailService.cancelSaveEmailAttachment(emailId, file).then(function (response) {
                                        if (file.Status === statusList.Uploading) {
                                            file.cancellationRequested = true;
                                            file.Status = statusList.Aborted;
                                            file.uploadPromise.abort();
                                        }
                                    });
                                }
                                break;
                        }

                        resolve();
                    });
                } else {
                    return EmailService.deleteEmailAttachment(file.EmailId, file.DocumentInformationId)
                        .then(deleteFileOk, deleteFileError);
                }

                function deleteFileOk(response) {
                    removeFileFromArray(file);
                }

                function deleteFileError(response) {
                    file.ErrorMessage = 'Error deleting the file';
                }
            }

            /**
             * Retries the upload of supplied file to the attachment list. This includes persisting the file data.
             *
             * @param {object} file - File details for the file to be retried
             *
             */
            function retryUpload(file) {
                if (file.FileSize <= vm.individualFileSizeLimit) {
                    file.Status = statusList.Waiting;

                    processUploadQueue();
                }
            }

            /**
             * Cancels all of the attachment uploads which are currently in progress or are of waiting status.
             *
             * @returns {promise} a promise which successfully resolves if no errors were experienced with deletion of the attachments.
             */
            function cancelUploadsInProgress() {
                return $q(function (resolve, reject) {
                    cancelUploadsInProgress.running = true;
                    var cancelList = _.filter(attachmentList, function (file) {
                        return (file.Status === statusList.Uploading || file.Status === statusList.Waiting);
                    });

                    angular.forEach(cancelList, function (file) {
                        remove(file);
                    });

                    cancelUploadsInProgress.running = false;
                    resolve();
                });
            }

            /**
             * @returns {bool} returns a bool stating if there is currently a file upload in progress.
             */
            function getUploadInProgress() {
                return _.some(attachmentList, { Status: statusList.Uploading });
            }

            /**
             * @returns {bool} returns a bool stating if there is at least one file in an error state or if the total file size of attachments is exceeded.
             */
            function getAttachmentHasError() {
                return _.some(attachmentList, { Status: statusList.Error }) || getTotalFileSizeExceeded();
            }

            /**
             * @returns {number} returns the attachment list length.
             */
            function getLength() {
                return attachmentList.length;
            }
            
            function removeFileFromArray(file) {
                _.remove(attachmentList, function (attachment) {
                    return attachment === file;
                });
            }

            /**
             *
             * Function which handles the upload queue. The function will continuely call itself until the queue has been finished.
             */
            function processUploadQueue() {
                if (!vm.uploadInProgress && !cancelUploadsInProgress.running) {
                    for (var i = 0; i < attachmentList.length; i++) {
                        if (angular.isDefined(attachmentList[i].Status) && !_.isNil(attachmentList[i].File) && !vm.uploadInProgress) {
                            if (attachmentList[i].Status === statusList.Waiting) {
                                attachmentList[i].Status = statusList.Uploading;
                                // Make a copy of the attachment index since it is not reliable to rely on 'i' when the promise has resolved.
                                var attachmentIndex = i;

                                getSaveAttachmentPromise(attachmentList[attachmentIndex])
                                                        .then(fileSaveOk, fileSaveError);
                            }
                        }
                    }
                }

                function fileSaveOk(data) {
                    attachmentList[attachmentIndex] = data;

                    if (attachmentList[attachmentIndex].cancellationRequested) {
                        remove(attachmentList[attachmentIndex]).then(function () {
                            processUploadQueue();
                        });
                    } else {
                        processUploadQueue();
                    }
                }

                function fileSaveError(response) {
                    // If there is an error response and there has been a cancellation request for the file, it should be removed from the list
                    if (attachmentList[attachmentIndex].cancellationRequested) {
                        removeFileFromArray(attachmentList[attachmentIndex]);
                    } else if (response.status === -1) {
                        if (angular.isDefined(attachmentList[attachmentIndex])) {
                            attachmentList[attachmentIndex].Status = statusList.Error;
                            attachmentList[attachmentIndex].ErrorMessage = 'Unknown error.';
                        }
                    } else {
                        if (angular.isDefined(attachmentList[attachmentIndex])) {
                            attachmentList[attachmentIndex].Status = statusList.Error;

                            // Determine where the error message is held and then append to file details.
                            var errorMessage = '';
                            if (angular.isDefined(response.data)) {
                                if (angular.isDefined(response.data.Message)) {
                                    errorMessage = response.data.Message;
                                } else {
                                    errorMessage = response.data;
                                }
                            }
                            attachmentList[attachmentIndex].ErrorMessage = errorMessage;
                        }
                        
                    }

                    processUploadQueue();
                }
            }

            // Construct the promise for saving the email attachment
            function getSaveAttachmentPromise(file) {
                var saveAttachmentPromise = null;

                // Local file
                if (file.File instanceof File) {
                    saveAttachmentPromise = EmailService.saveEmailAttachment(emailId, file);
                } else if (angular.isDefined(file.File.FileId)) {
                    // ChilliDocs file
                    file.UploadProgress = 100;
                    saveAttachmentPromise = EmailService.copyChilliDocsFileToEmail(emailId, file.File);
                }

                return saveAttachmentPromise;
            }

            /**
            *
            * Function which checks to see if the total file size of the attachment list has exceeded the limit.
            *
            * @returns {bool} returns true if the total file size has been exceeded.
            */
            function getTotalFileSizeExceeded() {
                var result = false;
                var totalFileSize = 0;

                angular.forEach(attachmentList, function (attachment) {
                    totalFileSize += attachment.FileSize;
                });

                if (totalFileSize > vm.totalFileSizeLimit) {
                    result = true;
                }

                return result;
            }
        }

        AttachmentList.prototype = {};
        AttachmentList.prototype.constructor = AttachmentList;

        return AttachmentList;
    }
})();